import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { moveStop, removeStop, useConfig } from '../lib/config.ts';
import { useSiteSearch } from '../lib/queries.ts';
import { MODE_LABEL } from '../lib/sl.ts';
import { Button, Card, Empty, Field, LineBadge, Spinner } from '../components/ui.tsx';

export function Setup() {
  const { stops } = useConfig();
  const [query, setQuery] = useState('');
  const search = useSiteSearch(query);
  const navigate = useNavigate();

  const saved = new Set(stops.map((s) => s.siteId));

  return (
    <>
      <header className="mb-4 flex items-baseline justify-between gap-3">
        <h1 className="text-2xl">Hållplatser</h1>
        <Link
          to="/"
          className="text-accent focus-visible:outline-accent rounded-lg text-[15px] font-semibold focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          Klar
        </Link>
      </header>

      <div className="space-y-3">
        {stops.map((stop, i) => (
          <Card key={stop.siteId} className="flex items-center gap-2 p-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-semibold">
                {stop.name}
                {(stop.note ?? stop.area) && (
                  <span className="text-faint ml-1.5 text-[13px] font-normal">
                    {[stop.note, stop.area].filter(Boolean).join(' · ')}
                  </span>
                )}
              </p>
              <p className="text-muted mt-1 flex flex-wrap items-center gap-1 text-[13px]">
                {stop.pairs.length === 0
                  ? 'Inga linjer valda'
                  : stop.pairs.map((p) => (
                      <LineBadge
                        key={`${p.line}:${p.directionCode}`}
                        line={p.line}
                        mode={p.mode}
                        className="min-w-8 px-1 py-0.5 text-[13px]"
                      />
                    ))}
              </p>
            </div>

            {/* Buttons rather than drag: two stops want one tap to swap, and a
                drag handle on a touch screen fights the page scroll. */}
            <div className="flex shrink-0 flex-col">
              <ReorderButton
                label="Flytta upp"
                disabled={i === 0}
                onClick={() => moveStop(stop.siteId, -1)}
                path="M6 15l6-6 6 6"
              />
              <ReorderButton
                label="Flytta ner"
                disabled={i === stops.length - 1}
                onClick={() => moveStop(stop.siteId, 1)}
                path="M6 9l6 6 6-6"
              />
            </div>

            <Link to={`/setup/${stop.siteId}`} className="shrink-0">
              <Button variant="ghost" className="px-3">
                Linjer
              </Button>
            </Link>
            <Button
              variant="danger"
              className="shrink-0 px-3"
              onClick={() => removeStop(stop.siteId)}
              aria-label={`Ta bort ${stop.name}`}
            >
              ✕
            </Button>
          </Card>
        ))}

        <Card className="p-4">
          <Field
            label="Lägg till hållplats"
            placeholder="Sök, t.ex. Odenplan"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            hint={
              search.isError
                ? undefined
                : 'Söker i hela SL:s nät. Fungerar utan uppkoppling.'
            }
          />

          {search.isError && (
            <p className="text-danger mt-2 text-[13px]">
              Hållplatslistan kunde inte laddas. Ladda om sidan.
            </p>
          )}

          {query.trim().length >= 2 && (
            <div className="mt-3">
              {search.isPending ? (
                <Spinner label="Laddar hållplatser" />
              ) : search.results.length === 0 ? (
                <Empty title="Inga träffar" />
              ) : (
                <ul className="divide-line divide-y">
                  {search.results.map((site) => (
                    <li key={site.id}>
                      <button
                        type="button"
                        onClick={() => navigate(`/setup/${site.id}`)}
                        className="focus-visible:outline-accent flex w-full items-center gap-2 py-2.5 text-left focus-visible:outline-2 focus-visible:-outline-offset-2 active:opacity-60"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[15px]">
                            {site.name}
                          </span>
                          {/* Where it is. On its own line rather than
                              trailing the name: 404 names are shared by two
                              or more stops, so for those this is the only
                              thing distinguishing the rows and it should not
                              be the part that gets truncated away. */}
                          {(site.area ?? site.note) && (
                            <span className="text-faint block truncate text-[13px]">
                              {[site.note, site.area].filter(Boolean).join(' · ')}
                            </span>
                          )}
                        </span>
                        {saved.has(site.id) && (
                          <span className="text-faint shrink-0 text-[13px]">
                            Tillagd
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Card>

        <p className="text-faint px-1 text-[13px]">
          Allt sparas bara i den här webbläsaren. Data från{' '}
          {Object.values(MODE_LABEL).slice(0, 3).join(', ').toLowerCase()} m.m. kommer
          från SL:s öppna API.
        </p>
      </div>
    </>
  );
}

function ReorderButton({
  label,
  path,
  disabled,
  onClick,
}: {
  label: string;
  path: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="text-faint focus-visible:outline-accent rounded-lg p-1 focus-visible:outline-2 disabled:opacity-25"
    >
      <svg
        viewBox="0 0 24 24"
        className="size-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d={path} />
      </svg>
    </button>
  );
}
